import React, { FC, useEffect, useMemo, useState } from "react";
import {
    Badge,
    Box,
    CircularProgress,
    Divider,
    IconButton,
    Menu,
    Stack,
    SxProps,
    Theme,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useAtom, useAtomValue } from "jotai";
import { baseAtom } from "../../stores/jotai/base";
import { fileUploadAtom, setUppyUploadRequestCount } from "../../stores/jotai/fileUpload";
import UploadStatusMenuItem from "./UploadStatusMenuItem";

interface IUploadStatusMenuProps {
    sx?: SxProps<Theme>;
}

const UploadStatusMenu: FC<IUploadStatusMenuProps> = (props) => {
    const { sx } = props;
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>(null);
    const [fileUploadAtomState, setFileUploadAtomState] = useAtom(fileUploadAtom);
    const base = useAtomValue(baseAtom);
    const isStatusMenuOpen = !!anchorEl;
    const uploadedFiles = base.uppyFiles;
    const uppy = fileUploadAtomState.uppyInstance;

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleMenuOpen = (anchor: HTMLButtonElement) => {
        setAnchorEl(anchor);
    };

    const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        handleMenuOpen(event.currentTarget);
    };

    const uploadingFileEntryList = useMemo(() => {
        let fileEntries = [];

        uploadedFiles.forEach((file, index) => {
            let progress = file?.progress?.percentage || "";
            let progressBar = parseInt(progress) || 0;

            if (progressBar < 100) {
                fileEntries.push(
                    <Box key={file?.id}>
                        <UploadStatusMenuItem file={file} />
                        {index !== uploadedFiles?.length - 1 && <Divider />}
                    </Box>
                );
            }
        });

        return fileEntries;
    }, [uploadedFiles]);

    useEffect(() => {
        if (uploadingFileEntryList?.length === 0) {
            handleMenuClose();
        }
    }, [uploadingFileEntryList]);

    const getFileUploadProgress = () => {
        const totalFilesToUpload = fileUploadAtomState.uppyUploadRequestCount;
        const fileUploadingCount = totalFilesToUpload || uploadedFiles?.length;
        const alreadyUploadeFiles = fileUploadingCount - uploadedFiles?.length;

        let totalPercentageUploaded = uploadedFiles.reduce((sum, file) => {
            const percentageUploaded = file?.progress?.percentage || "";
            const progress = parseInt(percentageUploaded) || 0;

            return sum + progress;
        }, 0);

        totalPercentageUploaded += (alreadyUploadeFiles * 100);

        return totalPercentageUploaded / fileUploadingCount;
    };

    useEffect(() => {
        uploadedFiles.forEach(file => {
            const _progress = file.progress;

            if (_progress.uploadComplete) {
                uppy?.removeFile(file.id);
            }
        });

        if (uploadedFiles.length === 0) {
            setUppyUploadRequestCount(setFileUploadAtomState, 0);
        }
    }, [uploadedFiles]);

    return (
        <>
            <Badge
                classes={{ badge: "appbar-nav-item-badge white-badge-text" }}
                badgeContent={
                    uploadingFileEntryList?.length > 0 ? (
                        <>
                            <CircularProgress
                                variant="determinate"
                                value={getFileUploadProgress()}
                                sx={{
                                    color: "white !important",
                                    height: "8px",
                                    width: "8px",
                                    maxWidth: "8px",
                                    minWidth: "8px",
                                    minHeight: "8px",
                                    maxHeight: "8px",
                                    position: "relative",
                                    left: "-2px",
                                }}
                            />
                            {uploadingFileEntryList.length}
                        </>
                    ) : undefined
                }
                color="error"
                sx={sx}
            >
                <IconButton onClick={handleButtonClick}>
                    <FileUploadOutlinedIcon
                        sx={{
                            fontSize: "24px",
                        }}
                    />
                </IconButton>
            </Badge>
            <Menu
                open={isStatusMenuOpen}
                anchorEl={anchorEl}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                MenuListProps={{
                    sx: {
                        minWidth: "300px",
                    },
                }}
            >
                {uploadingFileEntryList?.length > 0 ? (
                    uploadingFileEntryList
                ) : (
                    <Stack
                        sx={{
                            alignItems: "center",
                            padding: "10px",
                        }}
                    >
                        No uploads in progress.
                    </Stack>
                )}
            </Menu>
        </>
    );
};

export default UploadStatusMenu;
